import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MeetingProvider } from './contexts/MeetingContext';

// Components
import Login from './components/Login';
import Layout from './components/Layout';
import Stats from './components/Stats';
import SubtopicBrowser from './components/SubtopicBrowser';
import DocumentBrowser from './components/DocumentBrowser';
import StudyItemOverview from './components/StudyItemOverview';
import ConclusionMapping from './components/ConclusionMapping';
import CrossReferenceAnalysis from './components/CrossReferenceAnalysis';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center space-x-3">
          <div className="loading-spinner"></div>
          <span className="text-charcoal-600">Loading...</span>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// App Routes Component
const AppRoutes = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          isAuthenticated ? <Navigate to="/" replace /> : <Login />
        } 
      />
      
      {/* Protected Routes */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <Layout>
              <StudyItemOverview />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/home" 
        element={
          <ProtectedRoute>
            <Layout>
              <Stats />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/subtopics" 
        element={
          <ProtectedRoute>
            <Layout>
              <SubtopicBrowser />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/documents" 
        element={
          <ProtectedRoute>
            <Layout>
              <DocumentBrowser />
            </Layout>
          </ProtectedRoute>
        } 
      />

      
      <Route 
        path="/conclusions" 
        element={
          <ProtectedRoute>
            <Layout>
              <ConclusionMapping />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/cross-reference" 
        element={
          <ProtectedRoute>
            <Layout>
              <CrossReferenceAnalysis />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      {/* Fallback Route */}
      <Route 
        path="*" 
        element={
          <ProtectedRoute>
            <Layout>
              <div className="text-center py-12">
                <h1 className="text-2xl font-bold text-charcoal-900 mb-4">Page Not Found</h1>
                <p className="text-charcoal-600 mb-6">The page you're looking for doesn't exist.</p>
                <Navigate to="/" replace />
              </div>
            </Layout>
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
};

// Main App Component
const App = () => {
  return (
    <AuthProvider>
      <MeetingProvider>
        <Router>
          <div className="App">
            <AppRoutes />
            
            {/* Global Toast Notifications */}
            <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#fff',
                color: '#1f2124',
                border: '1px solid #e5e7eb',
                borderRadius: '0.75rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              },
              success: {
                iconTheme: {
                  primary: '#00d4aa',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#e60000',
                  secondary: '#fff',
                },
              },
            }}
          />
        </div>
      </Router>
      </MeetingProvider>
    </AuthProvider>
  );
};

export default App; 